import React from 'react';
import { TagBadge, type BadgeVariant } from '@chatsdet/ui';
import { formatDistanceToNow } from 'date-fns';
import type { ApiTraceSourceType } from '@chatsdet/types';

export { CopyButton } from '../../../ui/components/CopyButton';

const methodBadgeClassName = (method: string): string => {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'bg-blue-500/15 text-blue-700 border-blue-500/30';
    case 'POST':
      return 'bg-green-500/15 text-green-700 border-green-500/30';
    case 'PUT':
      return 'bg-orange-500/15 text-orange-700 border-orange-500/30';
    case 'PATCH':
      return 'bg-purple-500/15 text-purple-700 border-purple-500/30';
    case 'DELETE':
      return 'bg-red-500/15 text-red-700 border-red-500/30';
    default:
      return '';
  }
};

export const MethodBadge: React.FC<{ method: string }> = ({ method }) => (
  <TagBadge name={method} color="gray" hideDot size="sm" className={methodBadgeClassName(method)} />
);

export const statusBadgeVariant = (
  statusCode: number | null | undefined
): BadgeVariant => {
  if (typeof statusCode !== 'number') return 'outline';
  if (statusCode >= 200 && statusCode < 300) return 'success';
  if (statusCode >= 300 && statusCode < 400) return 'secondary';
  if (statusCode >= 400 && statusCode < 500) return 'warning';
  if (statusCode >= 500) return 'destructive';
  return 'outline';
};

export const formatRelativeTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return value;
  }
};

export const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const sourceTypeColors: Partial<Record<ApiTraceSourceType, string>> = {
  crawler: 'bg-blue-500/15 text-blue-700 border-blue-500/30',
  workflow: 'bg-green-500/15 text-green-700 border-green-500/30',
  import: 'bg-slate-500/15 text-slate-700 border-slate-500/30',
};

export const SourceTypeBadge: React.FC<{ sourceType: ApiTraceSourceType | null | undefined }> = ({
  sourceType,
}) => {
  if (!sourceType) return <span className="text-sm text-muted-foreground">—</span>;
  return (
    <TagBadge name={sourceType} color="gray" hideDot size="sm" className={sourceTypeColors[sourceType]} />
  );
};

export const StatusDots: React.FC<{ statusCodes: Array<number | null> }> = ({ statusCodes }) => {
  const dots = statusCodes.slice(0, 5);
  if (dots.length === 0) return <span className="text-sm text-muted-foreground">—</span>;

  return (
    <div className="flex items-center gap-1">
      {dots.map((code, i) => {
        let color = 'bg-slate-400';
        if (typeof code === 'number') {
          if (code >= 200 && code < 300) color = 'bg-green-500';
          else if (code >= 400 && code < 500) color = 'bg-amber-500';
          else if (code >= 500) color = 'bg-red-500';
          else color = 'bg-blue-400';
        }
        return <span key={i} className={`inline-block w-1.5 h-1.5 rounded-full ${color}`} />;
      })}
    </div>
  );
};

export const formatDuration = (ms: number | null | undefined): string => {
  if (typeof ms !== 'number') return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

